export const diagnosticSchemaVersion = 1 as const

export type DiagnosticIssueCode =
  | 'geocode_failed'
  | 'incorrect_results'
  | 'other_problem'
  | 'routing_degraded'
  | 'search_failed'
  | 'venue_unavailable'

export type DiagnosticPhase =
  | 'geocoding'
  | 'walking'
  | 'trips'
  | 'connections'
  | 'areas'
  | 'venues'
  | 'travel-times'
  | 'ranking'

type DiagnosticState = 'incomplete' | 'ready' | 'searching' | 'results' | 'partial' | 'empty' | 'error'
type ScheduleState = 'not_loaded' | 'ready' | 'unavailable'

export interface DiagnosticReport {
  schemaVersion: typeof diagnosticSchemaVersion
  kind: 'automatic' | 'manual'
  issueCode: DiagnosticIssueCode
  client: {
    build: string
    state: DiagnosticState
    online: boolean
    displayMode: 'browser' | 'standalone'
    viewport: 'phone' | 'tablet' | 'desktop'
    platform: 'android' | 'ios' | 'windows' | 'macos' | 'linux' | 'other'
    serviceWorkerControlled: boolean
  }
  operation: {
    type: 'app' | 'geocode' | 'search'
    outcome: 'failed' | 'partial' | 'degraded' | 'incorrect' | 'other'
    failedPhase: DiagnosticPhase | null
    errorCodes: DiagnosticIssueCode[]
    timings: Array<{ phase: DiagnosticPhase; durationMs: number }>
  }
  schedules: Record<'subway' | 'bus' | 'ferry' | 'multimodal', {
    state: ScheduleState
    identity?: string
  }>
  tripContext?: {
    participantCount: number
    venueType: 'coffee' | 'brunch' | 'drinks'
    timeIntent: 'leave-now' | 'depart-at' | 'arrive-by'
    travelModes: string[][]
  }
}

export interface DiagnosticReceipt {
  id: string
  receivedAt: string
  expiresAt: string
}

export class DiagnosticSubmissionError extends Error {
  constructor(message = 'Could not confirm whether the report was received. Copy the details instead.') {
    super(message)
  }
}

let automaticReports = 0
const automaticReportLimit = 3

export function clientDiagnosticState(state: DiagnosticState): DiagnosticReport['client'] {
  const userAgent = navigator.userAgent
  const displayMode = matchMedia('(display-mode: standalone)').matches
    || matchMedia('(display-mode: minimal-ui)').matches
    || ('standalone' in navigator && navigator.standalone === true)
    ? 'standalone' : 'browser'
  return {
    build: __BUILD_COMMIT__,
    state,
    online: navigator.onLine,
    displayMode,
    viewport: innerWidth <= 600 ? 'phone' : innerWidth <= 1024 ? 'tablet' : 'desktop',
    platform: /Android/i.test(userAgent) ? 'android'
      : /iPhone|iPad|iPod/i.test(userAgent) ? 'ios'
        : /Windows/i.test(userAgent) ? 'windows'
          : /Macintosh|Mac OS X/i.test(userAgent) ? 'macos'
            : /Linux/i.test(userAgent) ? 'linux' : 'other',
    serviceWorkerControlled: Boolean(navigator.serviceWorker?.controller),
  }
}

export async function submitDiagnostic(
  report: DiagnosticReport,
  timeoutMilliseconds = 8_000,
): Promise<DiagnosticReceipt> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMilliseconds)
  try {
    const response = await fetch('/api/diagnostics', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(report),
      cache: 'no-store',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      signal: controller.signal,
    })
    // Classify by status even when an intermediary returns HTML, never display response text.
    if (response.status === 429) {
      throw new DiagnosticSubmissionError('Report limit reached. Try again later, or copy the details.')
    }
    if (response.status === 503) {
      throw new DiagnosticSubmissionError('Problem reporting is unavailable. Copy the details instead.')
    }
    if (response.status >= 400 && response.status < 500) {
      throw new DiagnosticSubmissionError('Report was rejected. Copy the details instead.')
    }
    if (!response.ok) throw new DiagnosticSubmissionError()
    const body = await response.json() as Partial<DiagnosticReceipt>
    if (!body.id || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(body.id)
      || !body.receivedAt || !body.expiresAt
      || !Number.isFinite(Date.parse(body.receivedAt)) || !Number.isFinite(Date.parse(body.expiresAt))) {
      throw new DiagnosticSubmissionError()
    }
    return { id: body.id, receivedAt: body.receivedAt, expiresAt: body.expiresAt }
  } catch (cause) {
    if (cause instanceof DiagnosticSubmissionError) throw cause
    throw new DiagnosticSubmissionError()
  } finally {
    clearTimeout(timeout)
  }
}

export async function submitAutomaticDiagnostic(report: DiagnosticReport): Promise<DiagnosticReceipt | null> {
  if (automaticReports >= automaticReportLimit) return null
  automaticReports += 1
  try {
    return await submitDiagnostic(report)
  } catch {
    return null
  }
}
